import {AuthApiModule} from './AuthApiModule';
import {FirebaseAuthApiModule} from './FirebaseAuthApiModule';
import {config} from '../config';

const USE_FIREBASE = config.useFirebaseAuth;

/**
 * Hybrid Auth API Module
 * Firebase Auth when USE_FIREBASE_AUTH=true (window.__env__), otherwise the
 * backend's legacy JWT login. The legacy path goes away once every
 * environment signs in with Firebase.
 */
export class HybridAuthApiModule {
  private legacyAuth: AuthApiModule;
  private firebaseAuth: FirebaseAuthApiModule;

  constructor() {
    this.legacyAuth = new AuthApiModule();
    this.firebaseAuth = new FirebaseAuthApiModule();
  }

  async login(email: string, password: string) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.login(email, password);
    }
    return await this.legacyAuth.login(email, password);
  }

  async signInWithGoogle() {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.signInWithGoogle();
    }
    return {status: 400, success: false, data: null, message: 'Google sign-in requires Firebase auth'};
  }

  async forgotPassword(email: string) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.forgotPassword(email);
    }
    return await this.legacyAuth.forgotPassword(email);
  }

  async resetPassword(userId: string, resetPasswordCode: string, password: string) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.resetPassword(userId, resetPasswordCode, password);
    }
    return await this.legacyAuth.resetPassword(userId, resetPasswordCode, password);
  }

  async resendOtp(email: string) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.resendOtp(email);
    }
    return await this.legacyAuth.resendOtp(email);
  }

  async verifyOtp(email: string, otp: string) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.verifyOtp(email, otp);
    }
    return await this.legacyAuth.verifyOtp(email, otp);
  }

  async updatePassword(paramInfo: any) {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.updatePassword(paramInfo);
    }
    return await this.legacyAuth.updatePassword(paramInfo);
  }

  /** Current Firebase ID token, refreshed as needed; undefined for legacy JWT. */
  async getIdToken(): Promise<string | undefined> {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.getIdToken();
    }
    return undefined;
  }

  async logout() {
    if (USE_FIREBASE) {
      return await this.firebaseAuth.logout();
    }
    // Legacy logout is handled by clearing local storage
    return {status: 200, success: true, message: 'Logged out successfully'};
  }

  isUsingFirebase(): boolean {
    return USE_FIREBASE;
  }
}
